/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link } from 'react-router-dom';
import RequestResetLinkService from 'services/RequestResetLinkService';
import VerifyOtpAndResetPasswordService from 'services/VerifyOtpAndResetPasswordService';
import AppTextField from 'components/input-fields/AppTextField';
import PrimaryButton from 'components/buttons/PrimaryButton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';

const emailSchema = yup.object({
    email: yup
        .string()
        .trim()
        .email('Please enter a valid email address')
        .required('Email is required.'),
});

const verifySchema = yup.object({
    email: yup.string().trim().email().required(),
    otp: yup
        .string()
        .matches(/^\d{6}$/, 'Enter the 6-digit code')
        .required('Verification code is required'),
    password: yup
        .string()
        .min(6, 'Password must be at least 6 characters')
        .required('Password is required'),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password'), null], 'Passwords must match')
        .required('Please confirm your password'),
});

const Form = () => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [step, setStep] = useState('request');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const otpRefs = useRef([]);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [redirectMessage, setRedirectMessage] = useState('');
    const [countdown, setCountdown] = useState(5);
    const initialValues = {
        email: '',
        otp: '',
        password: '',
        confirmPassword: '',
    };

    const onSubmit = async (values) => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (step === 'request') {
            try {
                setLoading(true);
                await RequestResetLinkService({ email: values.email });
                setSuccessMessage('Request sent successfully!');
                setStep('verify');
                setLoading(false);
                setTimeout(() => {
                    otpRefs.current[0]?.focus();
                }, 200);
            } catch (error) {
                setLoading(false);
                const msg =
                    error?.response?.data?.message ||
                    error.message ||
                    'Failed to send reset link';
                setErrorMessage(msg);
            }
        } else {
            try {
                setLoading(true);
                await VerifyOtpAndResetPasswordService({
                    email: values.email,
                    otp: values.otp,
                    password: values.password,
                    password_confirmation: values.confirmPassword,
                });
                setErrorMessage(null);
                setRedirectMessage(
                    'Password updated successfully. Redirecting to login in 5 seconds...',
                );
                setSnackbarOpen(true);
                setCountdown(5);
            } catch (error) {
                setLoading(false);
                const msg =
                    error?.response?.data?.message ||
                    error.message ||
                    'Failed to update password';
                setErrorMessage(msg);
            }
        }
    };

    const formik = useFormik({
        initialValues,
        validationSchema: step === 'request' ? emailSchema : verifySchema,
        onSubmit,
        enableReinitialize: false,
    });

    useEffect(() => {
        const joined = otpDigits.join('');
        formik.setFieldValue('otp', joined);
    }, [otpDigits]);

    useEffect(() => {
        if (snackbarOpen) {
            if (countdown > 0) {
                setRedirectMessage(
                    `Password updated successfully. Redirecting to login in ${countdown} seconds...`,
                );
                const timer = setTimeout(
                    () => setCountdown(countdown - 1),
                    1000,
                );
                return () => clearTimeout(timer);
            } else {
                setSnackbarOpen(false);
                navigate('/signin');
            }
        }
    }, [snackbarOpen, countdown, navigate]);

    const handleOtpChange = (index, value) => {
        const v = value.replace(/\D/g, '').slice(0, 1);
        const next = [...otpDigits];
        next[index] = v;
        setOtpDigits(next);
        if (v && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (otpDigits[index]) {
                const next = [...otpDigits];
                next[index] = '';
                setOtpDigits(next);
            } else if (index > 0) {
                otpRefs.current[index - 1]?.focus();
                const next = [...otpDigits];
                next[index - 1] = '';
                setOtpDigits(next);
            }
        }
        if (e.key === 'ArrowLeft' && index > 0)
            otpRefs.current[index - 1]?.focus();
        if (e.key === 'ArrowRight' && index < 5)
            otpRefs.current[index + 1]?.focus();
    };

    const handleOtpPaste = (e) => {
        const paste = e.clipboardData
            .getData('text')
            .replace(/\D/g, '')
            .slice(0, 6);
        if (paste.length) {
            const next = ['', '', '', '', '', ''];
            for (let i = 0; i < paste.length; i++) next[i] = paste[i];
            setOtpDigits(next);
            e.preventDefault();
            otpRefs.current[Math.min(paste.length, 5)]?.focus();
        }
    };

    return (
        <Box>
            <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Box textAlign="center" mb={2}>
                            <Typography variant="h5" fontWeight={700}>
                                {step === 'request'
                                    ? 'Reset Your Password'
                                    : 'Request sent successfully!'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {step === 'request'
                                    ? 'Enter your email to receive a password reset link'
                                    : `We've sent a 6-digit confirmation email to your email.
                                        Please enter the code in the boxes below to verify your email.`}
                            </Typography>
                        </Box>

                        {errorMessage && (
                            <FormHelperText
                                error
                                sx={{ mb: 2, fontSize: 13, fontWeight: 500 }}
                            >
                                {errorMessage}
                            </FormHelperText>
                        )}

                        {successMessage && (
                            <FormHelperText
                                sx={{
                                    mb: 2,
                                    fontSize: 13,
                                    color: 'green',
                                    fontWeight: 500,
                                }}
                            >
                                {successMessage}
                            </FormHelperText>
                        )}

                        {step === 'request' ? (
                            <>
                                <Typography
                                    variant={'subtitle2'}
                                    sx={{ marginBottom: 2 }}
                                >
                                    Enter your email
                                </Typography>
                                <AppTextField
                                    label="Email *"
                                    variant="outlined"
                                    name={'email'}
                                    fullWidth
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.email &&
                                        Boolean(formik.errors.email)
                                    }
                                    helperText={
                                        formik.touched.email &&
                                        formik.errors.email
                                    }
                                />
                            </>
                        ) : (
                            <>
                                <AppTextField
                                    label="Email"
                                    variant="outlined"
                                    name={'email'}
                                    fullWidth
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    InputProps={{ readOnly: true }}
                                    disabled={true}
                                    sx={{ mb: 1 }}
                                />

                                <Box
                                    display="flex"
                                    justifyContent={'space-between'}
                                    my={2}
                                    onPaste={handleOtpPaste}
                                >
                                    {otpDigits.map((d, idx) => (
                                        <OutlinedInput
                                            key={idx}
                                            inputRef={(el) =>
                                                (otpRefs.current[idx] = el)
                                            }
                                            value={d}
                                            onChange={(e) =>
                                                handleOtpChange(
                                                    idx,
                                                    e.target.value,
                                                )
                                            }
                                            onKeyDown={(e) =>
                                                handleOtpKeyDown(idx, e)
                                            }
                                            inputProps={{
                                                maxLength: 1,
                                                inputMode: 'numeric',
                                                pattern: '[0-9]*',
                                                style: {
                                                    textAlign: 'center',
                                                    fontSize: 22,
                                                },
                                            }}
                                            sx={{
                                                width: 56,
                                                height: 56,
                                                borderRadius: 2,
                                            }}
                                            disabled={loading}
                                        />
                                    ))}
                                </Box>

                                {formik.touched.otp && formik.errors.otp && (
                                    <FormHelperText
                                        error
                                        sx={{ textAlign: 'center' }}
                                    >
                                        {formik.errors.otp}
                                    </FormHelperText>
                                )}

                                <AppTextField
                                    label="Password"
                                    variant="outlined"
                                    name={'password'}
                                    type={showPassword ? 'text' : 'password'}
                                    fullWidth
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.password &&
                                        Boolean(formik.errors.password)
                                    }
                                    helperText={
                                        formik.touched.password &&
                                        formik.errors.password
                                    }
                                    sx={{ mt: 2 }}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowPassword(
                                                            (v) => !v,
                                                        )
                                                    }
                                                    edge="end"
                                                >
                                                    {showPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <AppTextField
                                    label="Confirm New Password"
                                    variant="outlined"
                                    name={'confirmPassword'}
                                    type={
                                        showConfirmPassword
                                            ? 'text'
                                            : 'password'
                                    }
                                    fullWidth
                                    value={formik.values.confirmPassword}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.confirmPassword &&
                                        Boolean(formik.errors.confirmPassword)
                                    }
                                    helperText={
                                        formik.touched.confirmPassword &&
                                        formik.errors.confirmPassword
                                    }
                                    sx={{ mt: 2 }}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() =>
                                                        setShowConfirmPassword(
                                                            (v) => !v,
                                                        )
                                                    }
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? (
                                                        <VisibilityOff />
                                                    ) : (
                                                        <Visibility />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        )}
                    </Grid>

                    <Grid item container xs={12}>
                        <Box
                            display="flex"
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            alignItems={{ xs: 'stretched', sm: 'center' }}
                            justifyContent={'space-between'}
                            width={1}
                            maxWidth={600}
                            margin={'0 auto'}
                        >
                            <Box marginBottom={{ xs: 1, sm: 0 }}>
                                <Link
                                    style={{ textDecoration: 'none' }}
                                    to="/signin"
                                >
                                    <Button
                                        size={'large'}
                                        variant={'outlined'}
                                        fullWidth
                                    >
                                        Back to login
                                    </Button>
                                </Link>
                            </Box>
                            <PrimaryButton
                                size={'large'}
                                variant={'contained'}
                                type={'submit'}
                                disabled={loading}
                            >
                                {step === 'request'
                                    ? 'Send reset link'
                                    : 'Update Password'}
                            </PrimaryButton>
                        </Box>
                    </Grid>
                </Grid>
            </form>
            <Snackbar
                open={snackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    {redirectMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Form;
